/**
 * El estado de cuenta de un actor en un periodo (28-oct-2026): saldo inicial, cada movimiento con su saldo corrido,
 * saldo final y totales por concepto. Lo emite la base (`fn_drop_estado_de_cuenta`, 20261028009000); acá se lee, se
 * vuelve a cuadrar y se describe. El PDF (`elPdfDelEstadoDeCuenta.ts`) y el CSV salen de lo que hay acá.
 *
 * Plan maestro de operaciones, fase A paso 7: «estado de cuenta en PDF por actor». El founder: «como lo harían las
 * grandes empresas».
 *
 * ⭐ COMO LAS GRANDES (Stripe «Balance summary», Mercado Pago «Estado de cuenta», un banco): el documento es una
 *    igualdad que cualquiera puede rehacer con una calculadora — saldo inicial + abonos − cargos = saldo final — y cada
 *    línea dice de qué pedido es. Si la igualdad no se cumple, el documento no existe.
 *
 * 🚨 SE CUADRA DOS VECES, EN LA BASE Y ACÁ. La base se niega a emitir uno que no cuadre contra el libro; acá se rehace
 *    la cuenta con lo que llegó (`exigirElCuadre`) antes de dibujar un PDF o armar un CSV. Lo que se descarga es lo que
 *    se comprobó: un número que se pierde en el camino (un `numeric` mal convertido, una línea que se cae) frena la
 *    descarga en vez de dejar un papel que miente.
 *
 * 🔢 TODO EN CÉNTIMOS. La suma de cientos de montos en coma flotante no da exacto (0.1 + 0.2): se compara en enteros.
 *
 * ⭐ LO QUE SE DESCARGA ESTÁ EMITIDO (founder, 28-oct: «sí»): tiene número correlativo del negocio en ese papel y queda
 *    archivado (`fn_drop_emitir_estado_de_cuenta`). La vista previa no tiene número y no se descarga
 *    (`exigirQueEsteEmitido`). De una corrección llega su motivo para el negocio, nunca la nota interna.
 *
 * 🔒 LO QUE SE VE LO DECIDE LA BASE: el cliente sólo viene en el de la tienda, el depósito del courier sólo en el del
 *    courier, y nunca la nota libre del asiento. Acá no se agrega nada que no haya venido.
 */
import type { EstadoDeCuenta } from './estadosDeCuenta';

/** Las cuentas del libro que tiene un estado de cuenta (las de cada papel, `ESTADO_DE_CUENTA[papel].cuentas`). */
export type CuentaDelEstado = 'billetera' | 'por_recibir' | 'por_pagar' | 'almacen' | 'courier' | 'por_rendir';

/** Lo que dice la base de un pago al banco del propio negocio, o del depósito del propio courier. */
export interface ReferenciaDelMovimiento {
  /** Pago al banco: `por_pagar` (en camino), `pagado` o `anulado`. */
  estado?: string | null;
  /** El número de operación: del pago al banco, del depósito del courier o de la recarga de la tienda. */
  operacion?: string | null;
  pagado_en?: string | null;
  confirmado_en?: string | null;
}

export interface MovimientoDelEstado {
  n: number;
  /** El momento del asiento (ISO). */
  fecha: string;
  /** El día del asiento en Lima (AAAA-MM-DD). */
  dia: string;
  cuenta: CuentaDelEstado;
  /** El hecho del libro (`drop_libro_asientos.tipo`, o `pago_al_banco`, `pago_anulado`, `diferencia_del_deposito`, `credito_descontado`). */
  hecho: string;
  /** El concepto de la línea (`drop_libro_movimientos.concepto`). */
  concepto: string;
  pedido: string | null;
  /** Sólo en el de la tienda. */
  cliente: string | null;
  referencia: ReferenciaDelMovimiento | null;
  /**
   * De una corrección, el motivo PARA el negocio (`drop_libro_asientos.motivo`, 20261028009000). La nota interna nunca
   * llega. Las correcciones de antes, sin motivo: null («Corrección · concepto», como siempre).
   */
  motivo: string | null;
  /** A favor del negocio: positivo es un abono, negativo un cargo. */
  monto: number;
  /** El saldo después de este movimiento (todas las cuentas del papel). */
  saldo: number;
}

export interface SaldoDeLaCuenta {
  cuenta: CuentaDelEstado;
  saldo_inicial: number;
  abonos: number;
  cargos: number;
  saldo_final: number;
}

export interface TotalDelConcepto {
  concepto: string;
  movimientos: number;
  abonos: number;
  cargos: number;
  neto: number;
}

export interface EstadoDeCuentaDelPeriodo {
  /**
   * El número correlativo del negocio en ese papel (EC-91388-T-000012): lo da la EMISIÓN, que lo archiva. La vista
   * previa no tiene número (null) y no se descarga.
   */
  numero: string | null;
  correlativo: number | null;
  /** Lo devolvió el archivo: el mismo documento ya se había emitido (mismo número, misma fecha). */
  reemitido: boolean;
  emitido_en: string;
  /** El periodo ya terminó: no le pueden entrar más movimientos. */
  cerrado: boolean;
  negocio: { id: string; nombre: string };
  papel: EstadoDeCuenta;
  desde: string;
  hasta: string;
  cuentas: SaldoDeLaCuenta[];
  saldo_inicial: number;
  abonos: number;
  cargos: number;
  saldo_final: number;
  cuadra: boolean;
  huella: string;
  por_concepto: TotalDelConcepto[];
  movimientos: MovimientoDelEstado[];
}

// ── Lo que llega de la base ──────────────────────────────────────────────────────────────────────────────────────

const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) throw new Error(`El estado de cuenta trae un monto que no es un número: ${String(v)}`);
  return n;
};
const texto = (v: unknown): string | null => (v === null || v === undefined || v === '' ? null : String(v));

/**
 * El documento tal como lo devuelve `fn_drop_estado_de_cuenta`, con los montos como números.
 *
 * 🪤 Postgres puede mandar un `numeric` como texto (lo hace en las columnas; en un jsonb viaja como número). Se
 *    convierte una vez, acá, y un monto que no es número frena: no se dibuja «S/ NaN».
 */
export function leerElEstadoDeCuenta(crudo: unknown): EstadoDeCuentaDelPeriodo {
  const r = crudo as Record<string, unknown>;
  if (!r || typeof r !== 'object' || !Array.isArray(r.movimientos) || !Array.isArray(r.cuentas)) {
    throw new Error('La base no devolvió un estado de cuenta.');
  }
  const negocio = (r.negocio ?? {}) as Record<string, unknown>;
  return {
    numero: texto(r.numero),
    correlativo: r.correlativo === null || r.correlativo === undefined ? null : num(r.correlativo),
    reemitido: r.reemitido === true,
    emitido_en: String(r.emitido_en),
    cerrado: r.cerrado === true,
    negocio: { id: String(negocio.id ?? ''), nombre: String(negocio.nombre ?? '') },
    papel: r.papel as EstadoDeCuenta,
    desde: String(r.desde),
    hasta: String(r.hasta),
    cuentas: (r.cuentas as Record<string, unknown>[]).map((c) => ({
      cuenta: c.cuenta as CuentaDelEstado,
      saldo_inicial: num(c.saldo_inicial), abonos: num(c.abonos), cargos: num(c.cargos), saldo_final: num(c.saldo_final),
    })),
    saldo_inicial: num(r.saldo_inicial),
    abonos: num(r.abonos),
    cargos: num(r.cargos),
    saldo_final: num(r.saldo_final),
    cuadra: r.cuadra === true,
    huella: String(r.huella ?? ''),
    por_concepto: ((r.por_concepto ?? []) as Record<string, unknown>[]).map((c) => ({
      concepto: String(c.concepto), movimientos: num(c.movimientos), abonos: num(c.abonos), cargos: num(c.cargos), neto: num(c.neto),
    })),
    movimientos: (r.movimientos as Record<string, unknown>[]).map((m) => ({
      n: num(m.n),
      fecha: String(m.fecha),
      dia: String(m.dia),
      cuenta: m.cuenta as CuentaDelEstado,
      hecho: String(m.hecho),
      concepto: String(m.concepto),
      pedido: texto(m.pedido),
      cliente: texto(m.cliente),
      referencia: m.referencia && typeof m.referencia === 'object' ? (m.referencia as ReferenciaDelMovimiento) : null,
      motivo: texto(m.motivo),
      monto: num(m.monto),
      saldo: num(m.saldo),
    })),
  };
}

// ── El cuadre ────────────────────────────────────────────────────────────────────────────────────────────────────

/** Céntimos enteros: la única forma de comparar plata sumada sin el ruido de la coma flotante. */
export const centimos = (n: number): number => Math.round(n * 100);

export class ElEstadoDeCuentaNoCuadra extends Error {
  constructor(public readonly problemas: string[]) {
    super(`El estado de cuenta no cuadra y no se emite: ${problemas.join('; ')}`);
    this.name = 'ElEstadoDeCuentaNoCuadra';
  }
}

/**
 * Lo que no cuadra del documento, rehecho con sus propias líneas. Vacío = cuadra.
 *
 * Por cuenta y en total: saldo inicial + abonos − cargos = saldo final, con los abonos y cargos sumados de las líneas.
 * Y el saldo corrido: cada línea es el saldo anterior más su monto, y el último es el final.
 */
export function loQueNoCuadra(e: EstadoDeCuentaDelPeriodo): string[] {
  const problemas: string[] = [];
  if (!e.cuadra) problemas.push('la base no lo dio por cuadrado');
  const suma = (lista: number[]) => lista.reduce((a, n) => a + centimos(n), 0);

  for (const c of e.cuentas) {
    const lineas = e.movimientos.filter((m) => m.cuenta === c.cuenta).map((m) => m.monto);
    const abonos = suma(lineas.filter((n) => n > 0));
    const cargos = -suma(lineas.filter((n) => n < 0));
    if (abonos !== centimos(c.abonos)) problemas.push(`${c.cuenta}: los abonos dicen ${c.abonos} y las líneas suman ${abonos / 100}`);
    if (cargos !== centimos(c.cargos)) problemas.push(`${c.cuenta}: los cargos dicen ${c.cargos} y las líneas suman ${cargos / 100}`);
    if (centimos(c.saldo_inicial) + abonos - cargos !== centimos(c.saldo_final)) {
      problemas.push(`${c.cuenta}: ${c.saldo_inicial} + ${abonos / 100} − ${cargos / 100} no da ${c.saldo_final}`);
    }
  }
  const cuentas = new Set(e.cuentas.map((c) => c.cuenta));
  const ajenas = e.movimientos.filter((m) => !cuentas.has(m.cuenta));
  if (ajenas.length) problemas.push(`${ajenas.length} línea(s) de una cuenta que no es de este papel`);

  if (suma(e.cuentas.map((c) => c.saldo_inicial)) !== centimos(e.saldo_inicial)) problemas.push('el saldo inicial no es la suma de sus cuentas');
  if (suma(e.cuentas.map((c) => c.saldo_final)) !== centimos(e.saldo_final)) problemas.push('el saldo final no es la suma de sus cuentas');
  const abonos = suma(e.movimientos.map((m) => m.monto).filter((n) => n > 0));
  const cargos = -suma(e.movimientos.map((m) => m.monto).filter((n) => n < 0));
  if (abonos !== centimos(e.abonos) || cargos !== centimos(e.cargos)) problemas.push('los totales de abonos y cargos no son la suma de las líneas');
  if (centimos(e.saldo_inicial) + abonos - cargos !== centimos(e.saldo_final)) {
    problemas.push(`${e.saldo_inicial} + ${abonos / 100} − ${cargos / 100} no da el saldo final ${e.saldo_final}`);
  }

  let corrido = centimos(e.saldo_inicial);
  for (const m of e.movimientos) {
    corrido += centimos(m.monto);
    if (corrido !== centimos(m.saldo)) { problemas.push(`el saldo corrido se rompe en la línea ${m.n}`); break; }
  }
  if (corrido !== centimos(e.saldo_final)) problemas.push('el último saldo corrido no es el saldo final');

  const porConcepto = suma(e.por_concepto.map((c) => c.neto));
  if (porConcepto !== abonos - cargos) problemas.push('los totales por concepto no suman los movimientos');
  return problemas;
}

/** Frena si el documento no cuadra: nada se dibuja ni se descarga sin pasar por acá. */
export function exigirElCuadre(e: EstadoDeCuentaDelPeriodo): void {
  const problemas = loQueNoCuadra(e);
  if (problemas.length) throw new ElEstadoDeCuentaNoCuadra(problemas);
}

/**
 * ⭐ SÓLO SE DESCARGA LO EMITIDO (20261028009000): un PDF o un CSV sin número sería un papel que nadie puede rastrear.
 *    La vista previa se mira; lo que sale de la pantalla pasa antes por `fn_drop_emitir_estado_de_cuenta`.
 */
export function exigirQueEsteEmitido(e: EstadoDeCuentaDelPeriodo): asserts e is EstadoDeCuentaDelPeriodo & { numero: string } {
  if (!e.numero) throw new Error('Este estado de cuenta todavía no se emitió: no tiene número.');
}

/** Lo emitido de un negocio en un papel: el archivo (`fn_drop_estados_de_cuenta_emitidos`). */
export interface EstadoDeCuentaEmitido {
  id: string;
  numero: string;
  correlativo: number;
  desde: string;
  hasta: string;
  huella: string;
  cerrado: boolean;
  saldo_inicial: number;
  abonos: number;
  cargos: number;
  saldo_final: number;
  movimientos: number;
  emitido_en: string;
  /** Su nombre si lo emitió alguien del negocio; «Smarty» si lo emitió Smarty. */
  emitido_por: string | null;
}

export function leerLoEmitido(filas: unknown): EstadoDeCuentaEmitido[] {
  return ((filas ?? []) as Record<string, unknown>[]).map((f) => ({
    id: String(f.id), numero: String(f.numero), correlativo: num(f.correlativo), desde: String(f.desde), hasta: String(f.hasta),
    huella: String(f.huella), cerrado: f.cerrado === true,
    saldo_inicial: num(f.saldo_inicial), abonos: num(f.abonos), cargos: num(f.cargos), saldo_final: num(f.saldo_final),
    movimientos: num(f.movimientos), emitido_en: String(f.emitido_en), emitido_por: texto(f.emitido_por),
  }));
}

// ── Cómo se dice cada cosa ───────────────────────────────────────────────────────────────────────────────────────

export const NOMBRE_DEL_PAPEL: Record<EstadoDeCuenta, string> = {
  tienda: 'Tienda', proveedor: 'Proveedor', almacen: 'Almacén', courier: 'Courier',
};

/** El nombre de cada cuenta en el papel (formal: es un documento, no la billetera). */
export const NOMBRE_DE_LA_CUENTA_DEL_ESTADO: Record<CuentaDelEstado, string> = {
  billetera: 'Billetera',
  por_recibir: 'Por recibir',
  por_pagar: 'Por liquidar',
  almacen: 'Despachos',
  courier: 'Entregas',
  por_rendir: 'Por rendir',
};

const HECHO: Record<string, string> = {
  recarga: 'Recarga',
  consumo: 'Pago con saldo',
  pago: 'Pago del pedido',
  manejo: 'Manejo y despacho',
  cobro: 'Cobro contra entrega',
  rendicion: 'Liquidación del courier',
  comision: 'Comisión de la plataforma',
  no_entregado: 'No entregado',
  reintegro: 'Reintegro',
  devolucion: 'Devolución',
  ajuste: 'Corrección',
  liquidacion: 'Liquidación',
  no_llego: 'Pago que no llegó',
  pago_al_banco: 'Pago a tu banco',
  pago_anulado: 'Pago anulado: vuelve a tu saldo',
  diferencia_del_deposito: 'Diferencia del depósito',
  credito_descontado: 'Crédito descontado del depósito',
};

/** Los hechos que ya dicen todo: el concepto sería repetirlo («Recarga · recarga»). */
const HECHO_SOLO = new Set(['recarga', 'comision', 'pago_al_banco', 'pago_anulado', 'diferencia_del_deposito', 'credito_descontado', 'liquidacion']);

const CONCEPTO: Record<string, string> = {
  cobrado: 'cobrado al cliente',
  producto: 'producto',
  encargo: 'producto y despacho',
  fulfillment: 'fulfillment',
  envio: 'envío',
  comision: 'comisión',
  pos: 'comisión del POS',
  reembolso: 'reembolso',
  ajuste: 'ajuste',
  mercaderia: 'mercadería',
  recarga: 'recarga',
  retiro: 'pago a tu banco',
  rendicion: 'liquidación',
};

/** Lo que cambia de nombre según quién lo mira. */
const CONCEPTO_DEL_PAPEL: Partial<Record<EstadoDeCuenta, Record<string, string>>> = {
  proveedor: { producto: 'tu producto', encargo: 'tu producto', envio: 'despacho' },
  almacen: { envio: 'despacho y empaque' },
  courier: { envio: 'tarifa de entrega', cobrado: 'cobrado contra entrega' },
};

/**
 * El mismo hecho, contado por el que cobra. «Pago con saldo» es lo que hizo la tienda; para el proveedor esa misma
 * línea es una venta, y para el almacén, un encargo pagado.
 */
const HECHO_DEL_PAPEL: Partial<Record<EstadoDeCuenta, Record<string, string>>> = {
  proveedor: { consumo: 'Venta', pago: 'Venta', cobro: 'Venta contra entrega' },
  almacen: { consumo: 'Encargo pagado', pago: 'Encargo pagado', cobro: 'Contra entrega' },
};

const mayuscula = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);

/** El nombre de un concepto en los totales («Envío», «Tarifa de entrega»). */
export function nombreDelConcepto(concepto: string, papel: EstadoDeCuenta): string {
  return mayuscula(CONCEPTO_DEL_PAPEL[papel]?.[concepto] ?? CONCEPTO[concepto] ?? concepto.replace(/_/g, ' '));
}

/**
 * Qué fue el movimiento, en una línea: «Cobro contra entrega · envío», «Pago a tu banco».
 *
 * ⭐ LA LIQUIDACIÓN DEL COURIER SE DICE DESDE CADA LADO. Es un solo asiento: a la tienda le pasa la plata de «Por recibir»
 *    a su «Billetera» (dos líneas que suman cero); al courier le baja lo que debe y le cobra su tarifa en el depósito.
 */
export function detalleDelMovimiento(m: Pick<MovimientoDelEstado, 'hecho' | 'concepto' | 'cuenta' | 'monto'>, papel: EstadoDeCuenta): string {
  if (m.hecho === 'rendicion' && m.concepto === 'rendicion') {
    if (m.cuenta === 'por_recibir') return 'Liquidado: pasa a tu billetera';
    if (m.cuenta === 'billetera') return 'Liquidado: entra a tu billetera';
    if (m.cuenta === 'por_rendir') return 'Depósito a Smarty';
    if (m.cuenta === 'courier') return 'Tarifa descontada del depósito';
  }
  // Lo que el cliente pagó en la puerta, y lo que el courier gana por entregarlo: el cobro ya lo dice.
  if (m.hecho === 'cobro' && m.concepto === 'cobrado') return 'Cobrado contra entrega';
  if (m.hecho === 'cobro' && m.concepto === 'envio' && papel === 'courier') return 'Tarifa de entrega';
  // El pago directo de un encargo (20260921220000): la transferencia entra a la billetera y en el mismo acto la paga.
  if (m.hecho === 'pago' && m.concepto === 'recarga') return 'Transferencia para el pedido';
  const hecho = HECHO_DEL_PAPEL[papel]?.[m.hecho] ?? HECHO[m.hecho] ?? mayuscula(m.hecho.replace(/_/g, ' '));
  if (HECHO_SOLO.has(m.hecho)) return hecho;
  return `${hecho} · ${nombreDelConcepto(m.concepto, papel).toLowerCase()}`;
}

/**
 * El motivo de una corrección, el que es para el negocio (20261028009000). Las de antes no lo tienen: dicen sólo
 * «Corrección · concepto», como siempre. La nota interna nunca llega acá.
 */
export function motivoDelMovimiento(m: Pick<MovimientoDelEstado, 'hecho' | 'motivo'>): string | null {
  return m.hecho === 'ajuste' && m.motivo?.trim() ? m.motivo.trim() : null;
}

/** La referencia que acompaña al movimiento: el número de operación o en qué va el pago. */
export function referenciaDelMovimiento(m: Pick<MovimientoDelEstado, 'hecho' | 'referencia'>): string | null {
  const r = m.referencia;
  if (!r) return null;
  if (m.hecho === 'pago_al_banco' || m.hecho === 'pago_anulado') {
    if (r.estado === 'pagado') return r.operacion ? `Transferido · op. ${r.operacion}` : 'Transferido';
    if (r.estado === 'por_pagar') return 'En camino a tu banco';
    if (r.estado === 'anulado') return 'Pago anulado';
    return null;
  }
  return r.operacion ? `Op. ${r.operacion}` : null;
}

/** «a tu favor» o «a favor de Smarty»: el signo del saldo, en palabras. */
export function aFavorDe(saldo: number): string {
  return centimos(saldo) < 0 ? 'a favor de Smarty' : 'a tu favor';
}

// ── Fechas y montos ──────────────────────────────────────────────────────────────────────────────────────────────

/** 1234.5 → «1,234.50» (como los montos del banco en Perú). El signo va aparte. */
export function importe(n: number): string {
  const c = Math.abs(centimos(n));
  const entero = Math.floor(c / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${entero}.${String(c % 100).padStart(2, '0')}`;
}

/** Un saldo con su signo: «S/ 120.00», «-S/ 598.00». */
export function soles(n: number): string {
  return `${centimos(n) < 0 ? '-' : ''}S/ ${importe(n)}`;
}

/** «2026-10-27» → «27/10/2026». Sin zonas horarias: el día ya viene de Lima. */
export function fechaCorta(dia: string): string {
  const [a, m, d] = dia.slice(0, 10).split('-');
  return `${d}/${m}/${a}`;
}

/** El momento en Lima: «27/10/2026 14:05». */
export function momentoEnLima(iso: string): string {
  const partes = new Intl.DateTimeFormat('es-PE', {
    timeZone: 'America/Lima', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(iso));
  const p = (t: string) => partes.find((x) => x.type === t)?.value ?? '';
  return `${p('day')}/${p('month')}/${p('year')} ${p('hour')}:${p('minute')}`;
}

/**
 * El periodo en palabras. 🪤 «Todo» del selector arranca el 1-ene-2000 (`PolarisDateRangePopover`): un papel que diga
 *    «del 01/01/2000» se lee como un error, así que dice «todo el historial».
 */
export function periodoEnPalabras(desde: string, hasta: string): string {
  if (desde <= '2000-01-01') return `Todo el historial al ${fechaCorta(hasta)}`;
  if (desde === hasta) return `${fechaCorta(desde)}`;
  return `Del ${fechaCorta(desde)} al ${fechaCorta(hasta)}`;
}

/**
 * El nombre del archivo, con el número primero (como el extracto de un banco):
 * «EC-91388-T-000012-Vital-Natura-Plus-tienda-2026-10-01-2026-10-27.pdf». Sin número: «Estado-de-cuenta-…».
 */
export function nombreDelArchivo(e: Pick<EstadoDeCuentaDelPeriodo, 'negocio' | 'papel' | 'desde' | 'hasta'> & { numero?: string | null }, extension: 'pdf' | 'csv'): string {
  const negocio = e.negocio.nombre.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'negocio';
  return `${e.numero ?? 'Estado-de-cuenta'}-${negocio}-${e.papel}-${e.desde}-${e.hasta}.${extension}`;
}

// ── El CSV ───────────────────────────────────────────────────────────────────────────────────────────────────────

const celda = (v: string | number | null) => {
  let s = v === null ? '' : String(v);
  // 🪤 Lo que Excel correría como fórmula se neutraliza; un monto negativo (-2.00) es un número, no una fórmula.
  if (/^[=+\-@]/.test(s) && !/^-?\d+(\.\d+)?$/.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const decimal = (n: number) => `${centimos(n) < 0 ? '-' : ''}${importe(n).replace(/,/g, '')}`;

/**
 * El CSV del estado de cuenta: la primera fila es el saldo inicial y la última el final, como el extracto de un banco,
 * así la planilla cuadra sola (saldo inicial + Σ abonos − Σ cargos = saldo final). Lo frena el mismo cuadre que el PDF.
 */
export function csvDelEstadoDeCuenta(e: EstadoDeCuentaDelPeriodo): string {
  exigirElCuadre(e);
  exigirQueEsteEmitido(e);
  const conCliente = e.papel === 'tienda';
  const dosCuentas = e.cuentas.length > 1;
  const cabecera = ['Fecha', 'Pedido', ...(conCliente ? ['Cliente'] : []), 'Detalle', 'Motivo', ...(dosCuentas ? ['Cuenta'] : []), 'Referencia', 'Cargo', 'Abono', 'Saldo'];
  const fila = (v: (string | number | null)[]) => v.map(celda).join(',');
  const dato = (nombre: string, valor: string) => fila([nombre, valor, ...Array<null>(cabecera.length - 2).fill(null)]);
  // Las filas del saldo: sin cargo ni abono, así sumar esas columnas da los totales del periodo.
  const saldo = (dia: string, detalle: string, n: number) => fila([
    fechaCorta(dia), null, ...(conCliente ? [null] : []), detalle, null, ...(dosCuentas ? [null] : []), null, null, null, decimal(n),
  ]);
  const lineas = [
    dato('Estado de cuenta', e.numero),
    dato('Negocio', e.negocio.nombre),
    dato('Papel', NOMBRE_DEL_PAPEL[e.papel]),
    dato('Periodo', periodoEnPalabras(e.desde, e.hasta)),
    dato('Emitido', `${momentoEnLima(e.emitido_en)} (Lima)`),
    dato('Huella', e.huella),
    '',
    fila(cabecera),
    saldo(e.desde, 'Saldo inicial', e.saldo_inicial),
    ...e.movimientos.map((m) => fila([
      fechaCorta(m.dia),
      m.pedido,
      ...(conCliente ? [m.cliente] : []),
      detalleDelMovimiento(m, e.papel),
      motivoDelMovimiento(m),
      ...(dosCuentas ? [NOMBRE_DE_LA_CUENTA_DEL_ESTADO[m.cuenta]] : []),
      referenciaDelMovimiento(m),
      m.monto < 0 ? decimal(-m.monto) : null,
      m.monto > 0 ? decimal(m.monto) : null,
      decimal(m.saldo),
    ])),
    saldo(e.hasta, 'Saldo final', e.saldo_final),
  ];
  return lineas.join('\r\n');
}
